import React, { useState } from 'react';
import { BookOpen, Award, Clock, TrendingUp } from 'lucide-react';
import { useDashboard } from '@/hooks/reporting/useDashboard';
import MetricsCard from '@/components/reports/cards/MetricsCard';
import ProgressBar from '@/components/reports/charts/ProgressBar';
import LineChart from '@/components/reports/charts/LineChart';
import PeriodTypeSelector from '@/components/reports/filters/PeriodTypeSelector';
import DateRangePicker from '@/components/reports/filters/DateRangePicker';
import CacheIndicator from '@/components/reports/common/CacheIndicator';
import EmptyState from '@/components/reports/common/EmptyState';
import type { PeriodType, LearnerProgressRequest } from '@/types/reporting-v2';

export default function LearnerProgress() {
  const [params, setParams] = useState<LearnerProgressRequest>({
    period_type: 'monthly',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    end_date: new Date().toISOString(),
  });

  const { data, isLoading, error, refetch } = useDashboard('learner-progress', params);

  const handlePeriodChange = (period: PeriodType) => {
    setParams((prev) => ({ ...prev, period_type: period }));
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setParams((prev) => ({ ...prev, start_date: start, end_date: end }));
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading dashboard</h3>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Learning Progress</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track your learning journey and achievements
          </p>
        </div>
        {data?.cache_metadata && (
          <CacheIndicator
            cacheMetadata={data.cache_metadata}
            onRefresh={() => refetch()}
            loading={isLoading}
          />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Period
            </label>
            <PeriodTypeSelector value={params.period_type} onChange={handlePeriodChange} />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <DateRangePicker
              startDate={params.start_date}
              endDate={params.end_date}
              onChange={handleDateRangeChange}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
                  <div className="h-8 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !data ? (
        <EmptyState
          icon={BookOpen}
          title="No data available"
          description="Unable to load your learning progress. Please try again later."
        />
      ) : (
        <>
          {/* Personal Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricsCard
              title="Courses Enrolled"
              value={data.personal_metrics.enrolled_courses.toLocaleString()}
              icon={BookOpen}
              color="primary"
              loading={false}
            />
            <MetricsCard
              title="Completion Rate"
              value={`${data.personal_metrics.completion_rate}%`}
              change={data.personal_metrics.completion_trend}
              icon={TrendingUp}
              color="success"
              loading={false}
            />
            <MetricsCard
              title="Certifications"
              value={data.personal_metrics.certifications_earned.toLocaleString()}
              change={data.personal_metrics.certifications_trend}
              icon={Award}
              color="warning"
              loading={false}
            />
            <MetricsCard
              title="Learning Hours"
              value={`${data.personal_metrics.total_learning_hours}h`}
              change={data.personal_metrics.hours_trend}
              icon={Clock}
              color="info"
              loading={false}
            />
          </div>

          {/* Learning Activity Trend */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Learning Activity</h2>
            <LineChart
              data={data.learning_activity.map((item) => ({
                label: item.period_label,
                values: {
                  'Hours Spent': item.hours_spent,
                  'Courses Completed': item.courses_completed,
                },
              }))}
              height={300}
            />
          </div>

          {/* Course Progress */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Courses</h2>
            <div className="space-y-4">
              {data.active_courses.map((course) => (
                <div key={course.course_id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">{course.course_name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Last activity: {new Date(course.last_activity).toLocaleDateString()}
                      </p>
                    </div>
                    {course.due_date && (
                      <span className="text-xs text-gray-500">
                        Due: {new Date(course.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <ProgressBar
                    label="Progress"
                    value={course.progress_percentage}
                    size="sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Achievements */}
          {data.recent_achievements.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Recent Achievements
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.recent_achievements.map((achievement, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200"
                  >
                    <Award className="h-8 w-8 text-yellow-500 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-900">{achievement.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
                      <span className="text-xs text-gray-500 mt-2 block">
                        {new Date(achievement.earned_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Skill Progress */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Skill Development</h2>
            <div className="space-y-4">
              {data.skill_progress.map((skill) => (
                <ProgressBar
                  key={skill.skill_name}
                  label={skill.skill_name}
                  value={skill.proficiency_level}
                  trend={skill.trend}
                  size="md"
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
